import {Component, OnInit, Input} from '@angular/core';
import { UntypedFormGroup, UntypedFormArray, UntypedFormControl, Validators, UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {WizardWidgetComponent} from '../wizard-widget/wizard-widget.component';
import {TypesService, FormBuilderService} from '../../../services';
import {debounceTime, first} from 'rxjs/operators';
import {BasicIncomeModel} from '../../../models/widgets/employment-details-model';
import _ from 'lodash';
import {BusinessIncomeModel} from '../../../models/widgets';
import {noSameValues} from '../../../_global/validators/custom-validators.directive';
import {DataHandlerService} from '../../../services/data-handler.service';
import { SelfEmployedDetailsComponent } from '../self-employed-details/self-employed-details.component';
import { BasActivityDetailsComponent } from '../bas-activity-details/bas-activity-details.component';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ValidationPartialComponent } from '../partials/validation-partial.component';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { NgIf, NgFor, UpperCasePipe } from '@angular/common';

@Component({
    selector: 'app-income-details',
    templateUrl: './income-details.component.html',
    styles: [],
    standalone: true,
    imports: [NgIf, MatCardModule, MatCardModule, NgFor, FormsModule, ReactiveFormsModule, MatButtonModule, MatButtonModule, MatFormFieldModule,
      MatFormFieldModule, MatSelectModule, MatSelectModule, MatOptionModule, MatOptionModule, ValidationPartialComponent, MatInputModule,
      MatInputModule, MatIconModule, SelfEmployedDetailsComponent, BasActivityDetailsComponent, UpperCasePipe]
})

/**
 * This component handles company incomes
 */
export class IncomeDetailsComponent extends WizardWidgetComponent implements OnInit {
  @Input() public formGroup: UntypedFormGroup;
  @Input() public personList: UntypedFormArray;
  @Input() public companyList: UntypedFormArray;
  @Input() public trustList: UntypedFormArray;
  @Input() public isSMSF = false;
  @Input() public featureSwitches: any[] = [];

  get basIncomeTypeEnabled(): boolean {
    return !!(this.featureSwitches && this.featureSwitches.find(x => x.id === 'fits-3477_bas-income-type')?.ext?.enabled);
  }

  public incomeSourceTypeSlug = 'income-source-types';
  public frequencyTypesSlug = 'frequencies-vx';
  public otherIncomeSourceTypes = [];
  public smsfIncomeSourceTypes = [];
  public frequencyTypes = [];

  constructor(private typesService: TypesService,
              private formBuilder: UntypedFormBuilder,
              public dataHandlerService: DataHandlerService,
              private formBuilderService: FormBuilderService) {
    super(dataHandlerService);
  }

  ngOnInit() {
    super.ngOnInit();
    const companyArray = this.formGroup.get('companyIncomeList') as UntypedFormArray;
    const trustArray = this.formGroup.get('trustIncomeList') as UntypedFormArray;
    this.populateIncomeSourceTypes();
    this.populateFrequencyTypes();

    this.initialFGSetup(companyArray);
    this.initialFGSetup(trustArray);
    companyArray.controls.forEach(fg => this.ensureBasControls(fg as UntypedFormGroup));
    trustArray.controls.forEach(fg => this.ensureBasControls(fg as UntypedFormGroup));
    companyArray.controls.forEach(fg => this.setupIncomeTypeBehaviors(fg as UntypedFormGroup));
    trustArray.controls.forEach(fg => this.setupIncomeTypeBehaviors(fg as UntypedFormGroup));

    this.companyList.valueChanges
      .pipe(debounceTime(500))
      .subscribe((val: any[]) => {
        console.debug('IncomeDetailsComponent.companyList.valueChanges');
        if (val.length !== this.formGroup.value.companyIncomeList.length) {
          this.updateCompanyIncomeDetails(companyArray, val.length);
        }
      });

    this.trustList.valueChanges
      .pipe(debounceTime(500))
      .subscribe((val: any[]) => {
        console.debug('IncomeDetailsComponent.companyList.valueChanges');
        if (val.length !== this.formGroup.value.trustIncomeList.length) {
          this.updateCompanyIncomeDetails(trustArray, val.length);
        }
      });
  }

  initialFGSetup(formArray) {
    formArray.controls.forEach(fg => {
      fg = this.formBuilder.group({
        previousYearIncome: [null, Validators.compose([])],
        previousYearTotalIncome: [null, Validators.compose([])],
        currentYearIncome: [null, Validators.compose([])],
        currentYearTotalIncome: [null, Validators.compose([])],
        allowableAddBacks: new UntypedFormArray([...fg.controls['allowableAddBacks'].controls], noSameValues('addbackType', [{
          'name': 'other-company',
          maxCount: 2
        }]))
      });

      if (!fg.value.allowableAddBacks) {
        console.log('setupParentValidationAndCalcs no addbacks');
        fg.setControl('allowableAddBacks', new UntypedFormArray([]));
      }

      if (!fg.value.otherIncomes) {
        fg.setControl('otherIncomes', new UntypedFormArray([]));
      }

      if (!fg.value.basQuarters) {
        fg.setControl('basQuarters', new UntypedFormArray([]));
      }

      fg.get('allowableAddBacks').setValidators(noSameValues('addbackType', [{
        'name': 'other-company',
        maxCount: 2
      }]));
    });
  }

  updateCompanyIncomeDetails(formArray: UntypedFormArray, desiredCount: number) {
    if (formArray.controls.length < desiredCount) {
      this.addIncomeDetail(formArray);
    } else if (formArray.controls.length > desiredCount) {
      const diff = formArray.controls.length - desiredCount;
      for (let i = 0; i < diff; i++) {
        formArray.removeAt(1);
      }
    }
  }

  /** Ensure BAS controls are present on a company/trust income FormGroup (guards against session-restored FormGroups missing these controls). */
  ensureBasControls(fg: UntypedFormGroup) {
    if (!fg.get('basIncomeType')) {
      fg.addControl('basIncomeType', new UntypedFormControl('business-income'));
    }
    if (!fg.get('mostRecentBasPeriod')) {
      fg.addControl('mostRecentBasPeriod', new UntypedFormControl(null));
    }
    if (!fg.get('basQuarters')) {
      fg.addControl('basQuarters', new UntypedFormArray([]));
    }
    if (!fg.get('numberOfQuarters')) {
      fg.addControl('numberOfQuarters', new UntypedFormControl(2));
    }
    if (!fg.get('businessIncome')) {
      fg.addControl('businessIncome', new UntypedFormControl(null));
    }
  }

  /** Subscribe to basIncomeType changes and clear stale data when the user switches modes. */
  setupIncomeTypeBehaviors(fg: UntypedFormGroup) {
    const applyBasActivityValidators = (incomeType: string, emit: boolean) => {
      if (incomeType === 'business-activity') {
        fg.get('mostRecentBasPeriod').setValidators(Validators.required);
        fg.get('mostRecentBasPeriod').updateValueAndValidity({emitEvent: emit});
        fg.get('businessIncome').setValidators(Validators.required);
        fg.get('businessIncome').updateValueAndValidity({emitEvent: emit});
        fg.get('previousYearIncome').patchValue(null, {emitEvent: false});
        fg.get('previousYearTotalIncome').patchValue(null, {emitEvent: false});
        const addbacks = fg.get('allowableAddBacks') as UntypedFormArray;
        while (addbacks && addbacks.length) {
          addbacks.removeAt(0);
        }
      } else {
        fg.get('mostRecentBasPeriod').clearValidators();
        fg.get('mostRecentBasPeriod').patchValue(null, {emitEvent: false});
        fg.get('mostRecentBasPeriod').updateValueAndValidity({emitEvent: emit});
        fg.get('businessIncome').clearValidators();
        fg.get('businessIncome').patchValue(null, {emitEvent: false});
        fg.get('businessIncome').updateValueAndValidity({emitEvent: emit});
        const bqArray = fg.get('basQuarters') as UntypedFormArray;
        while (bqArray.length) {
          bqArray.removeAt(0);
        }
        if (fg.get('numberOfQuarters')) {
          fg.get('numberOfQuarters').patchValue(2, {emitEvent: false});
        }
      }
    };

    // Apply immediately for the current value (handles page refresh / session restore)
    applyBasActivityValidators(fg.get('basIncomeType')?.value, false);

    fg.get('basIncomeType').valueChanges
      .subscribe((incomeType: string) => applyBasActivityValidators(incomeType, true));
  }

  addIncomeDetail(formArray) {
    const fg = this.formBuilderService.convertObjectToFormGroup(new BusinessIncomeModel());
    this.formBuilderService.setValidationRules(new BusinessIncomeModel(), fg);
    fg.addControl('allowableAddBacks', new UntypedFormArray([]));
    fg.get('allowableAddBacks').setValidators(noSameValues('addbackType', [{
      'name': 'other-company',
      maxCount: 2
    }]));
    fg.addControl('otherIncomes', new UntypedFormArray([]));
    this.ensureBasControls(fg);
    this.setupIncomeTypeBehaviors(fg);
    formArray.push(fg);
    return fg;
  }

  public addIncome(formArray) {
    const fg = this.formBuilderService.convertObjectToFormGroup(new BasicIncomeModel());
    this.formBuilderService.setValidationRules(new BasicIncomeModel(), fg);
    formArray.push(fg);

    console.log('SelfEmployedDetailsComponent', this.formGroup);
  }

  public removeIncome(formArray, index) {
    const arrayControl = formArray as UntypedFormArray;
    arrayControl.removeAt(index);
  }

  public populateIncomeSourceTypes() {
    return this.typesService.getTypesList(this.incomeSourceTypeSlug)
      .then(results => {
        const incomeSourceTypes = results.filter((x: any) => !x.ext.immutable);
        const otherIncomeTypes = ['salary', 'other', 'nonspousal', 'interst-addback'];
        this.otherIncomeSourceTypes = incomeSourceTypes.filter(x => x.ext.incomeCategory && otherIncomeTypes.includes(x.ext.incomeCategory) && x.ext.companyIncome && !x.ext.isSMSF);
        this.smsfIncomeSourceTypes = _.filter(incomeSourceTypes, ['ext.isSMSF', true]);
      });
  }

  public populateFrequencyTypes() {
    return this.typesService.getTypesList(this.frequencyTypesSlug)
      .then(results => {
        results.filter(type => {
          const excludes = ['maturity', 'daily'];
          if (type.ext.Active === true && !excludes.includes(type.id)) {
            this.frequencyTypes.push(type);
          }
        });
      });
  }

  protected readonly first = first;
}
